import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  CardActions,
  Chip,
  IconButton,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Favorite from "@mui/icons-material/Favorite";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import FormatTime from "../AdsPage/FormatTime";
import LoadingAnimation from "../Animations/LoadingAnimation";
import {
  cardHover,
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
  primaryButtonStyles,
  secondaryButtonStyles,
  semiBoldFonts,
} from "../../Styles/Styles";

export default function SimilarAds({ adDetails }) {
  const { uniqueId } = useParams();
  const [similarAds, setSimilarAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setLoggedInUser(user);
    if (user) {
      setFavorites(user.favorites || []);
    }
  }, []);

  const userId = loggedInUser ? loggedInUser._id : null;

  useEffect(() => {
    const fetchSimilarAds = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/similar/${uniqueId}`
        );
        if (!response.ok) throw new Error("Failed to fetch similar ads");
        const similarAdsData = await response.json();
        setSimilarAds(similarAdsData);
      } catch (error) {
        console.error("Error fetching similar ads:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSimilarAds();
  }, [uniqueId]);

  const handleAddToFavorites = async (uniqueId) => {
    if (!userId) {
      alert("Please log in to add to favorites.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/favorite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ uniqueId: uniqueId }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/singleuser/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const updatedUser = await userResponse.json();
        if (userResponse.ok) {
          localStorage.setItem("user", JSON.stringify(updatedUser));
          setLoggedInUser(updatedUser);
          setFavorites(updatedUser.favorites || []);
        }
        alert(data.message);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const isFavorite = (uniqueId) => favorites.includes(uniqueId);

  const handleViewAdClick = (uniqueId) => {
    navigate(`/ad/${uniqueId}`);
  };

  const handleMakeOffer = (uniqueId) => {
    navigate(`/ad/${uniqueId}`, { state: { showModal: true } });
  };

  return (
    <>
      {loading ? (
        <LoadingAnimation />
      ) : (
        <>
          {similarAds.length > 0 && (
            <Card
              sx={{
                mt: 3,
                mb: 5,
                p: 2,
                ...cardOutline,
              }}
            >
              <Box>
                {/* Heading and Navigation Icons in Same Line */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="h5"
                    component="div"
                    gutterBottom
                    sx={{
                      mb: 2,
                      ...headingFonts,
                      flex: 1, // Ensure title takes remaining space
                    }}
                  >
                    Similar Products
                  </Typography>

                  {/* Navigation Icons in Front of Similar Ads Section */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end", // Align arrows to the right
                    }}
                  >
                    <IconButton
                      className="prev-arrow"
                      sx={{
                        color: "var(--primary-color)",
                        ...noOutline,
                      }}
                    >
                      <ArrowBackIosNewIcon />
                    </IconButton>
                    <IconButton
                      className="next-arrow"
                      sx={{
                        color: "var(--primary-color)",
                        ...noOutline,
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Swiper Slider */}
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={25}
                  slidesPerView={3}
                  navigation={{
                    prevEl: ".prev-arrow",
                    nextEl: ".next-arrow",
                  }}
                  breakpoints={{
                    1024: { slidesPerView: 3 }, // Large screens (desktops)
                    768: { slidesPerView: 2 }, // Medium screens (tablets)
                    0: { slidesPerView: 1 }, // Small & extra-small screens (mobile)
                  }}
                >
                  {similarAds.map((similarAd) => (
                    <SwiperSlide key={similarAd._id}>
                      <Card
                        sx={{
                          maxWidth: "auto",
                          position: "relative",
                          mb: 2,
                          ...cardOutline,
                          ...cardHover,
                        }}
                      >
                        {similarAd.isSold && (
                          <div
                            style={{
                              top: "0",
                              position: "absolute",
                              zIndex: 1,
                              fontSize: "0.8rem",
                              fontWeight: "700",
                              backgroundColor: "#ff0019",
                              color: "white",
                              padding: "5px 10px",
                            }}
                          >
                            Sold
                          </div>
                        )}
                        <CardMedia
                          sx={{
                            height: 180,
                            cursor: "pointer",
                            backgroundColor: "#F1F3F4",
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            backgroundImage: `url(${similarAd.images[0].thumbnail})`,
                          }}
                          title={similarAd.title}
                          onClick={() => handleViewAdClick(similarAd.uniqueId)}
                        />
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Chip
                              label={`$ ${similarAd.price.toLocaleString()}`}
                              sx={{
                                ...semiBoldFonts,
                                fontSize: "1rem",
                                color: "white",
                                borderRadius: "0",
                                backgroundColor: "var(--primary-color)",
                              }}
                            />
                            <IconButton
                              sx={{
                                color: "var(--primary-color)",
                                fontSize: "1.5rem",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleAddToFavorites(similarAd.uniqueId)
                              }
                            >
                              {isFavorite(similarAd.uniqueId) ? (
                                <Favorite />
                              ) : (
                                <FavoriteBorder />
                              )}
                            </IconButton>
                          </Box>

                          <Typography
                            gutterBottom
                            variant="body1"
                            component="div"
                            onClick={() =>
                              handleViewAdClick(similarAd.uniqueId)
                            }
                            sx={{
                              ...headingFonts,
                              cursor: "pointer",
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              WebkitLineClamp: 2,
                              lineHeight: "1.2em",
                              maxHeight: "2.4em",
                            }}
                          >
                            <strong>{similarAd.title}</strong>
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              ...fonts,
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5em",
                            }}
                          >
                            <CategoryIcon
                              sx={{
                                fontSize: "1em",
                                color: "var(--secondary-color)",
                              }}
                            />
                            {similarAd.category}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              ...fonts,
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5em",
                            }}
                          >
                            <LocationOnIcon
                              sx={{
                                fontSize: "1em",
                                color: "var(--secondary-color)",
                              }}
                            />
                            {similarAd.location}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              ...fonts,
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5em",
                            }}
                          >
                            <ScheduleIcon
                              sx={{
                                fontSize: "1em",
                                color: "var(--secondary-color)",
                              }}
                            />
                            <FormatTime createdAt={similarAd.createdAt} />
                          </Typography>
                        </CardContent>
                        <CardActions
                          sx={{
                            pl: 2,
                            mb: 1,
                            marginTop: "-10px",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              ...primaryButtonStyles,
                            }}
                            onClick={() =>
                              handleViewAdClick(similarAd.uniqueId)
                            }
                          >
                            View ad
                          </Button>
                          {!similarAd.isSold && similarAd.receiveOffers && (
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                ...secondaryButtonStyles,
                              }}
                              onClick={() =>
                                handleMakeOffer(similarAd.uniqueId)
                              }
                            >
                              Make offer
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            </Card>
          )}
        </>
      )}
    </>
  );
}
